// Import React hooks
import { useEffect, useMemo, useState } from "react";

// Import api calls
import {
    fetchConnectionPages,
} from "api/APIStrichCalls";

// Import css
import "./ConnectionPages.css";

// Import components
import RichTextInput from "global/components/RichTextInput";
import CreateButton from "global/components/CreateButton";
import { CloseIcon } from "global/strich_icons/StrichIcons";
import HelpButton from "global/components/HelpButton";

// Create the page groups used by the UI
const pageTypeGroups = ["Policies", "Store Content", "Landing Pages", "Other"];



// Function to render connection pages
const RenderConnectionPages = ({ connectionDetails, token, formValues, setFormValues }) => {

    // Store the connection pages
    //const [connectionPages, setConnectionPages] = useState([]);

    // Store whether pages are loading
    const [isLoadingPages, setIsLoadingPages] = useState(true);

    // Store the selected page id
    const [selectedPageId, setSelectedPageId] = useState(null);

    // Store whether the mobile page panel is open
    const [isMobilePanelOpen, setIsMobilePanelOpen] = useState(false);

    // Store the dragged page id
    const [draggedPageId, setDraggedPageId] = useState(null);

    // Store the page currently being edited
    const [editingPage, setEditingPage] = useState(null);

    

    // Get selected page
    const selectedPage = useMemo(() => {

        if (!selectedPageId) {

            // Clear the selected page
            return null;

        } else {

            // Return the selected page from the current pages list
            return formValues?.pages?.find((page) => page.id === selectedPageId) || null;

        }

    }, [formValues, selectedPageId]);



    // Group the pages by type
    const groupedPages = useMemo(() => {

        // Create grouped pages object
        const groups = {};

        // Loop through each group name
        pageTypeGroups.forEach((groupName) => {

            // Set each group as an empty array
            groups[groupName] = [];

        });

        // Loop through the connection pages
        formValues?.pages
            ?.filter((page) => page.change_type !== "deleted")
            ?.forEach((page) => {

            // Get the page type or use fallback
            const pageType = page?.type || "Other";

            // Get the safe group name
            const groupName = pageTypeGroups.includes(pageType) ? pageType : "Other";

            // Add the page to the correct group
            groups[groupName].push(page);

        });

        // Return the grouped pages
        return groups;

    }, [formValues?.pages]);



    // Fetch connection pages
    const getConnectionPages = async () => {

        // Set loading state
        setIsLoadingPages(true);

        // Fetch pages from the api
        const pagesResponse = await fetchConnectionPages(token, connectionDetails?.id);

        // Check if the api call worked
        if (pagesResponse.success) {

            // Store the sorted pages
            const sortedPages = [...pagesResponse?.results]
                .sort((firstPage, secondPage) => {

                    // Sort by ordering value
                    return Number(firstPage.ordering || 0) - Number(secondPage.ordering || 0);

                })
                .map((page, index) => {

                    // Add local clean change type
                    return {
                        ...page,
                        change_type: "none",
                        // If this is the user's page it will have an id but the Strich pages won't so create one using the slug and index
                        id: page.id ? page.id : index + page?.slug,
                    };

                });

            // Store the pages
            //setConnectionPages(sortedPages);

            setFormValues((previousValues) => ({
                ...previousValues,
                pages: sortedPages,
            }));

            // Select the first page by default
            setSelectedPageId(sortedPages[0]?.id || null);

        } else {

            // Toast error here
            console.error("Could not fetch connection pages.");

        }

        // End loading state
        setIsLoadingPages(false);

    };



    // Handle selecting a page
    const handleSelectPage = (page) => {

        // Store selected page id
        setSelectedPageId(page.id);

        // Store the selected page as the editing page
        setEditingPage(page);

        // Open the mobile panel
        setIsMobilePanelOpen(true);

    };



    // Handle changing an editable page field
    const handleEditingPageChange = (fieldName, fieldValue) => {

        // Update the selected page inside form values
        setFormValues((previousValues) => ({
            ...previousValues,
            pages: previousValues.pages.map((page) => {

                // Return unchanged page if it is not selected
                if (page.id !== selectedPageId) {

                    // Return unchanged page
                    return page;

                }

                // Return updated page
                return {
                    ...page,
                    [fieldName]: fieldValue,
                    change_type: page.change_type === "created" ? "created" : "updated",
                };

            }),
        }));

    };



    // Handle drag start
    const handleDragStart = (pageId) => {

        // Store dragged page id
        setDraggedPageId(pageId);

    };



    // Handle drag over
    const handleDragOver = (event) => {

        // Prevent default to allow drop
        event.preventDefault();

    };



    // Handle drop
    const handleDrop = (targetPageId) => {

        // Stop if no dragged page exists
        if (!draggedPageId) {

            // Return early
            return;

        }

        // Stop if dropping onto itself
        if (draggedPageId === targetPageId) {

            // Clear dragged page id
            setDraggedPageId(null);

            // Return early
            return;

        }

        // Get current page list
        const updatedPages = [...formValues?.pages];

        // Find dragged page index
        const draggedIndex = updatedPages.findIndex((page) => page.id === draggedPageId);

        // Find target page index
        const targetIndex = updatedPages.findIndex((page) => page.id === targetPageId);

        // Stop if either page is missing
        if (draggedIndex === -1 || targetIndex === -1) {

            // Clear dragged page id
            setDraggedPageId(null);

            // Return early
            return;

        }

        // Remove the dragged page
        const [draggedPage] = updatedPages.splice(draggedIndex, 1);

        // Insert dragged page in the target position
        updatedPages.splice(targetIndex, 0, draggedPage);

        // Add updated ordering values
        const reorderedPages = updatedPages.map((page, pageIndex) => {

            // Return page with updated ordering
            return {
                ...page,
                ordering: pageIndex + 1,
                change_type: page.change_type === "created" ? "created" : "updated",
            };

        });

        // Store reordered pages
        setFormValues((previousValues) => ({
            ...previousValues,
            pages: reorderedPages,
        }));
        
        // Clear dragged page id
        setDraggedPageId(null);

        // Save ordering to api here
        //console.log("Save reordered pages:", reorderedPages);

    };



    // Handle saving page
    const handleSavePage = async () => {

        // Stop if no editable page exists
        if (!editingPage) {

            // Return early
            return;

        }

        // Stop if page is not user editable
        if (editingPage.source !== "user") {

            // Return early
            return;

        }

        // Save page to api here
        //console.log("Save page:", editingPage);

        // Update local state after successful save
        setFormValues((previousValues) => ({
            ...previousValues,
            pages: previousValues.pages.map((page) => {

            // Return updated page if ids match
            if (page.id === editingPage.id) {

                // Return editing page
                return editingPage;

            }

            // Return unchanged page
            return page;

        }),

        }));

        /*
        setConnectionPages((currentPages) => currentPages.map((page) => {

            // Return updated page if ids match
            if (page.id === editingPage.id) {

                // Return editing page
                return editingPage;

            }

            // Return unchanged page
            return page;

        }));
        */

    };



    // Handle deleting page
    const handleDeletePage = async (pageToDelete) => {

        // Stop if no page was passed in
        if (!pageToDelete) {

            // Return early
            return;

        }

        // Stop if page is not user editable
        if (pageToDelete.source !== "user") {

            // Return early
            return;

        }

        // Create the updated pages array
        const updatedPages = formValues?.pages?.reduce((pages, page) => {

            // Check if this is not the page being deleted
            if (page.id !== pageToDelete.id) {

                // Keep the page
                return [...pages, page];

            }

            // Check if this page was created locally and has not been saved yet
            if (page.change_type === "created") {

                // Remove it completely because the API does not know about it yet
                return pages;

            }

            // Keep existing API pages, but mark them as deleted
            return [
                ...pages,
                {
                    ...page,
                    change_type: "deleted",
                },
            ];

        }, []) || [];

        // Create visible pages only
        const visiblePages = updatedPages.filter((page) => page.change_type !== "deleted");

        // Store updated pages
        setFormValues((previousValues) => ({
            ...previousValues,
            pages: updatedPages,
        }));

        // Check if the deleted page was selected
        if (selectedPageId === pageToDelete.id) {

            // Get the next visible page
            const nextSelectedPage = visiblePages[0] || null;

            // Store the next selected page id
            setSelectedPageId(nextSelectedPage?.id || null);

            // Store the next selected page
            setEditingPage(nextSelectedPage);

        }

        // Close mobile panel if there are no visible pages
        if (!visiblePages.length) {

            // Close mobile panel
            setIsMobilePanelOpen(false);

        }

    };



    // Handle add page
    const handleAddPage = () => {

        // Create temporary page
        const newPage = {
            id: `new-${Date.now()}`,
            ordering: formValues?.pages?.length + 1,
            type: "Store Content",
            content: "<h1>New Page</h1><p>Add your content here.</p>",
            image_id: null,
            image_url: "",
            name: "New Page",
            slug: "",
            source: "user",
            change_type: "created",
        };

        // Add page locally
        //setConnectionPages((currentPages) => [...currentPages, newPage]);

        setFormValues((previousValues) => ({
            ...previousValues,
            pages: [...previousValues?.pages, newPage]
        }));

        // Select new page
        setSelectedPageId(newPage.id);

        // Set editing page
        setEditingPage(newPage);

        // Open mobile panel
        setIsMobilePanelOpen(true);

    };



    // Fetch the pages once on mount
    useEffect(() => {

        // Fetch connection pages
        getConnectionPages();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);



    return (

        <div className="card">

             <div className="card__header">

                <h3>Store pages <HelpButton section="stores" code="pages-and-socials" label="Store Pages and Social Links" /></h3>

                <span className="hintText">

                    Manage storefront policies, content pages and landing pages

                </span>

            </div>

            <div className="card__content">

                <div className="connectionPages">

                    {isLoadingPages ? (

                        <div className="connectionPages__empty">
                            Loading pages...
                        </div>

                    ) : (

                        <div className="connectionPages__layout">

                            <div className="connectionPages__list">

                                <div className="connectionPages__add-button">

                                    <CreateButton
                                        title="+ Add page"
                                        size="s"
                                        type="button"
                                        onClick={handleAddPage}
                                    />

                                </div>

                                {pageTypeGroups.map((groupName) => {

                                    // Get pages for this group
                                    const pagesForGroup = groupedPages[groupName];

                                    // Do not render empty groups
                                    if (!pagesForGroup?.length) {

                                        // Return nothing
                                        return null;

                                    }

                                    return (

                                        <div
                                            className="connectionPages__group"
                                            key={groupName}
                                        >

                                            <p className="connectionPages__group-title">
                                                {groupName}
                                            </p>

                                            {pagesForGroup.map((page) => {

                                                // Check whether page is selected
                                                const isSelected = selectedPageId === page.id;

                                                // Check whether page is fully editable
                                                const canFullyEditPage = page.source === "user";

                                                return (

                                                    <div
                                                        className={`connectionPages__page-card ${isSelected ? "connectionPages__page-card--selected" : ""}`}
                                                        draggable={true}
                                                        key={page.id}
                                                        type="button"
                                                        onClick={() => handleSelectPage(page)}
                                                        onDragStart={() => handleDragStart(page.id)}
                                                        onDragOver={handleDragOver}
                                                        onDrop={() => handleDrop(page.id)}
                                                    >

                                                        <span className="connectionPages__drag-handle">
                                                            ↕
                                                        </span>

                                                        <span className="connectionPages__page-main">

                                                            <span className="connectionPages__page-name">
                                                                {page.name}
                                                            </span>

                                                            <span className="connectionPages__page-slug">
                                                                /{page.slug}
                                                            </span>

                                                        </span>

                                                        {!canFullyEditPage &&
                                                        
                                                            <span>
                                                                🔒
                                                            </span>
                                                        
                                                        }

                                                        <button
                                                            type="button"
                                                            title="Delete Page"
                                                            onClick={(e) => {
                                                                
                                                                e.stopPropagation();
                                                                
                                                                // Delete the page
                                                                handleDeletePage(page);

                                                            }}
                                                        >
                                                            🗑️
                                                        </button>

                                                    </div>

                                                );

                                            })}

                                        </div>

                                    );

                                })}

                            </div>

                            <div className="connectionPages__detail connectionPages__detail--desktop">

                                <RenderPageDetail
                                    editingPage={selectedPage}
                                    handleDeletePage={handleDeletePage}
                                    handleEditingPageChange={handleEditingPageChange}
                                    handleSavePage={handleSavePage}
                                />

                            </div>

                        </div>

                    )}

                    {isMobilePanelOpen && selectedPage ? (
                        

                        <div className="connectionPages__mobile-overlay">

                             <div className="popup-overlay">

                                <div className="popup-container popup-container--large">

                                    {/* This is the modal header */}
                                    <div className="popup__header">
                    
                                        {/* This is the title area */}
                                        <div className="popup__headerLeft">
                                                                
                                            {/* This is the parent title */}
                                            <h2 className="popup__title">

                                                {editingPage.name}

                                            </h2>
                    
                                            {/* This is the parent subtitle */}
                                            <p className="popup__subtitle">

                                                /{editingPage.slug}

                                            </p>
                                                                
                                        </div>
                    
                                        {/* This is the header actions */}
                                        <div className="popup__headerRight">
                            
                                            {/* This is the close button */}
                                            <button type="button" className="popup__close" onClick={() => setIsMobilePanelOpen(false)} title="Close">
                                                <CloseIcon />
                                            </button>
                    
                                        </div>
                    
                                    </div>

                                    <div className="popup-content popup-content__body">

                                        <RenderPageDetail
                                            editingPage={selectedPage}
                                            handleDeletePage={handleDeletePage}
                                            handleEditingPageChange={handleEditingPageChange}
                                            handleSavePage={handleSavePage}
                                            fromMobilePanel={true}
                                        />

                                    </div>

                                </div>

                            </div>

                        </div>

                    ) : null}

                </div>

            </div>

        </div>

    );

};



// Function to render selected page detail
const RenderPageDetail = ({
    editingPage,
    handleDeletePage,
    handleEditingPageChange,
    handleSavePage,
    fromMobilePanel = false,
}) => {

    // Flag for viewing the page content as HTML or rendered nicely
    const [viewAsHTML, setViewAsHTML] = useState(false);

    const handleEditingPageContent = (value) => {

        handleEditingPageChange("content", value);

    }

    // Stop if no page is selected
    if (!editingPage) {

        return (

            <div className="connectionPages__empty">
                Select a page to preview or edit.
            </div>

        );

    }

    // Check whether page is fully editable
    const canFullyEditPage = editingPage.source === "user";

    return (

        <div className="connectionPages__detail-inner">

            <div className="connectionPages__detail-header">

                {!fromMobilePanel && 

                    <div>

                        <h3 className="connectionPages__detail-title">
                            {editingPage.name}
                        </h3>

                        <p className="connectionPages__detail-subtitle">
                            /{editingPage.slug}
                        </p>

                    </div>

                }

                {!canFullyEditPage &&

                    <span className={`connectionPages__status ${canFullyEditPage ? "connectionPages__status--editable" : "connectionPages__status--locked"}`}>
                        🔒 Locked
                    </span>

                }

            </div>

            {!canFullyEditPage ? (

                <div className="connectionPages__notice">
                    This page is managed by the platform because it may be required for checkout or store compliance.
                </div>

            ) : null}


            {/* Taken out becuase this is more like an icon image someone can click on to get to the page
            {editingPage.image_url ? (

                <div className="connectionPages__image-container">

                    <img
                        alt={editingPage.name}
                        className="connectionPages__image"
                        src={editingPage.image_url}
                    />

                    {canFullyEditPage &&

                        <button 
                            title="Remove Image"
                            onClick={() => {
                                handleEditingPageChange("image_url", "");
                                handleEditingPageChange("image_id", "");
                            }}
                            className="connectionPages__remove-image-button"
                        >
                            🗑️
                        </button>

                    }

                </div>

            ) : 
            
                <RenderImageUploader 
                    label="page-image"
                    selectedImage={editingPage.image_url || null}
                    onImageChange={(payload) => {

                        handleEditingPageChange("image_url", payload.imageBucketUrl);

                        handleEditingPageChange("image_id", payload.imageBucketId);

                    }}
                />
                
            }
            */}

            {canFullyEditPage ? (

                <div className="connectionPages__form">

                    <label className="connectionPages__field">

                        <span className="connectionPages__label">
                            Page name
                        </span>

                        <input
                            className="connectionPages__input"
                            type="text"
                            value={editingPage.name || ""}
                            onChange={(event) => handleEditingPageChange("name", event.target.value)}
                        />

                    </label>

                    <label className="connectionPages__field">

                        <span className="connectionPages__label">
                            URL Slug
                        </span>

                        <input
                            className="connectionPages__input"
                            type="text"
                            value={editingPage.slug || ""}
                            onChange={(event) => handleEditingPageChange("slug", event.target.value)}
                        />

                    </label>

                    <label className="connectionPages__field">

                        <span className="connectionPages__label">
                            Page type
                        </span>

                        <select
                            className="connectionPages__input"
                            value={editingPage.type || "Store Content"}
                            onChange={(event) => handleEditingPageChange("type", event.target.value)}
                        >

                            <option value="Store Content">
                                Store Content
                            </option>

                            <option value="Landing Pages">
                                Landing Pages
                            </option>

                        </select>

                    </label>
                    
                    <div className="connectionPages__field">

                        <div className="horizontal-align-content-center flex-justify-left-right">

                            <span className="connectionPages__label">
                                Content
                            </span>

                            <button
                                className="connectionPages__view-toggle"
                                type="button"
                                title={viewAsHTML ? "Display as final view" : "Display as HTML"}
                                onClick={(event) => {

                                    // Stop the click from bubbling to the popup/form
                                    event.stopPropagation();

                                    // Toggle between HTML and editor view
                                    setViewAsHTML((previousValue) => !previousValue);

                                }}
                            >
                                ⇄

                            </button>

                        </div>

                        {viewAsHTML ? 

                            <textarea
                                className="connectionPages__textarea"
                                value={editingPage.content || ""}
                                onChange={(event) => handleEditingPageChange("content", event.target.value)}
                            />

                        :

                            <RichTextInput
                                value={editingPage.content || ""}
                                onChange={handleEditingPageContent} 
                                disabled={!canFullyEditPage}
                                placeholder="Enter text..."
                            />

                        }

                    </div>

                </div>
               
            ) : (

                <div
                    className="connectionPages__preview"
                    dangerouslySetInnerHTML={{ __html: editingPage.content || "" }}
                />

            )}

        </div>

    );

};

export default RenderConnectionPages;